import {
	ChecklistOutlined,
	DeleteForeverOutlined,
	DeviceHubOutlined,
	FeedbackOutlined,
	MoreVert,
	NoteAddOutlined,
	PermIdentityOutlined,
} from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
	Box,
	ListItemIcon,
	Menu,
	MenuItem,
	MenuList,
	Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { useState } from "react";
import { toast } from "sonner";

import { useDeleteContractMutation } from "AdvisoryManagement/Api/Mutations/useDeleteContractMutation";
import { useProductCategoriesQuery } from "AdvisoryManagement/Api/Queries/useProductCategoriesQuery";
import { ContractDocumentsTable } from "AdvisoryManagement/Components/Client/Contracts/ContractDocumentsTable";
import { AddDocument } from "AdvisoryManagement/Components/Documents/AddDocument";
import { useAmlSdk } from "AdvisoryManagement/Hooks/useAmlSdk";
import { StatusChip } from "AdvisoryManagement/Shared/Status/StatusChip";
import { getStatusTitle } from "AdvisoryManagement/Shared/Status/statusUtils";
import type { DummyExtendedContract } from "AdvisoryManagement/types";
import { CommissionDistributionDialog } from "CommissionDistribution/Components/CommissionDistributionDialog";

const amlTooltips: Record<DummyExtendedContract["essentials"]["aml"], string> =
	{
		Ready: "Schváleno",
		Pending: "Čeká na vyhodnocení",
		NotReady: "Nevyplněno",
	};

const essentialsStatusColors: Record<
	DummyExtendedContract["essentials"]["aml"],
	{ backgroundColor: string; borderColor: string; color: string }
> = {
	Ready: {
		backgroundColor: "#22C55E1A",
		borderColor: "#22C55E",
		color: "#16A34A",
	},
	Pending: {
		backgroundColor: "#F59E0B1A",
		borderColor: "#F59E0B",
		color: "#D97706",
	},
	NotReady: {
		backgroundColor: "transparent",
		borderColor: "#D1D5DB",
		color: "#6B7280",
	},
};

type ContractsTableRowProps = {
	contract: DummyExtendedContract;
	defaultOpen?: boolean;
};

const AML_TEST_CONTROL_PROCEDURE_ID = "184047ca-3b24-4255-b4d0-a3676bc0e637";

export const ContractsTableRow = ({
	contract,
	defaultOpen,
}: ContractsTableRowProps) => {
	const productCategories = useProductCategoriesQuery();
	const { isLoaded: amlSdkLoaded, displayQuestionnaire } = useAmlSdk();

	const [open, setOpen] = useState(defaultOpen || false);

	return (
		<>
			<TableRow>
				<TableCell>
					<IconButton
						aria-label="expand row"
						size="small"
						onClick={() => setOpen(!open)}
						title="Zobrazit dokumenty"
						color="primary"
					>
						{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</TableCell>
				<TableCell component="th" scope="row">
					{productCategories.data?.find((c) => c.code === contract.categoryCode)
						?.name || "Investice"}
				</TableCell>
				<TableCell>
					<Box
						sx={{
							display: "flex",
							gap: 0.5,
							flexWrap: "wrap",
							alignItems: "center",
						}}
					>
						{contract.essentials.aml && (
							<StatusChip
								label={
									<>
										<Typography variant="inherit" component="span">
											AML
										</Typography>
										<PermIdentityOutlined sx={{ fontSize: "16px" }} />
									</>
								}
								title={amlTooltips[contract.essentials.aml]}
								onClick={() =>
									displayQuestionnaire(AML_TEST_CONTROL_PROCEDURE_ID)
								}
								disabled={!amlSdkLoaded}
								sx={{
									cursor: "pointer",
									...essentialsStatusColors[contract.essentials.aml],
								}}
							/>
						)}
						{contract.essentials.control && (
							<StatusChip
								label={
									<>
										<Typography variant="inherit" component="span">
											Kontrola
										</Typography>
										<ChecklistOutlined sx={{ fontSize: "16px" }} />
									</>
								}
								title={getStatusTitle(contract.essentials.control)}
							/>
						)}
						{contract.essentials.provision && (
							<CommissionDistributionStatusChip
								contract={contract}
								provisionStatus={contract.essentials.provision}
							/>
						)}
					</Box>
				</TableCell>
				<TableCell>
					<StatusChip label={getStatusTitle(contract.status)} />
				</TableCell>
				<ActionsCell contract={contract} />
			</TableRow>
			<ContractDocumentsTable
				contractId={contract.contractId}
				documents={contract.documents ?? []}
				open={open}
			/>
		</>
	);
};

type ActionsCellProps = {
	contract: DummyExtendedContract;
};

const ActionsCell = ({ contract }: ActionsCellProps) => {
	const contractId = contract.contractId;
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const menuOpen = Boolean(menuAnchorEl);
	const [addDocumentOpen, setAddDocumentOpen] = useState(false);
	const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);

	const deleteContractMutation = useDeleteContractMutation();

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
		setMenuAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
	};

	const handleCreateContract = () => {
		handleCloseMenu();
	};

	const handleOpenAddDocument = () => {
		setAddDocumentOpen(true);
		handleCloseMenu();
	};

	const handleCloseAddDocument = () => {
		setAddDocumentOpen(false);
	};

	const handleOpenCommissionDialog = () => {
		setCommissionDialogOpen(true);
		handleCloseMenu();
	};

	const handleCloseCommissionDialog = () => {
		setCommissionDialogOpen(false);
	};

	const handleDeleteContract = () => {
		deleteContractMutation
			.mutateAsync(contractId)
			.then(() => {
				toast.success("Smlouva byla smazána");
				handleCloseMenu();
			})
			.catch(() => {
				toast.error("Nastala chyba při mazání smlouvy");
			});
	};

	return (
		<TableCell>
			<Box>
				<IconButton size="small" onClick={handleOpenMenu}>
					<MoreVert />
				</IconButton>
				<Menu
					anchorEl={menuAnchorEl}
					open={menuOpen}
					onClose={handleCloseMenu}
					slotProps={{ paper: { sx: { p: 0 } } }}
				>
					<MenuList dense>
						<MenuItem onClick={handleOpenCommissionDialog}>
							<ListItemIcon>
								<NoteAddOutlined fontSize="small" />
							</ListItemIcon>
							Nastavit rozdělení provize
						</MenuItem>
						<MenuItem onClick={handleCreateContract}>
							<ListItemIcon>
								<ChecklistOutlined fontSize="small" />
							</ListItemIcon>
							Kontrola návazností smlouvy
						</MenuItem>
						<MenuItem onClick={handleOpenAddDocument}>
							<ListItemIcon>
								<NoteAddOutlined fontSize="small" />
							</ListItemIcon>
							Přidat dokument
						</MenuItem>
						<MenuItem onClick={handleCreateContract}>
							<ListItemIcon>
								<FeedbackOutlined fontSize="small" />
							</ListItemIcon>
							Intervence, reklamace
						</MenuItem>
						<MenuItem
							onClick={handleDeleteContract}
							sx={{ color: "error.main" }}
							disabled={deleteContractMutation.isPending}
						>
							<ListItemIcon sx={{ color: "error.main" }}>
								<DeleteForeverOutlined fontSize="small" />
							</ListItemIcon>
							Smazat smlouvu
						</MenuItem>
					</MenuList>
				</Menu>
				{addDocumentOpen && (
					<AddDocument
						contractId={contractId}
						onClose={handleCloseAddDocument}
					/>
				)}
				{commissionDialogOpen && (
					<CommissionDistributionDialog
						contract={contract}
						onClose={handleCloseCommissionDialog}
					/>
				)}
			</Box>
		</TableCell>
	);
};

type CommissionDistributionStatusChipProps = {
	contract: DummyExtendedContract;
	provisionStatus: "Ready" | "Pending" | "NotReady";
};

const provisionTooltips: Record<
	CommissionDistributionStatusChipProps["provisionStatus"],
	string
> = {
	Ready: "Rozdělení provize potvrzeno",
	Pending: "Rozdělení provize vyplněno, čeká na potvrzení",
	NotReady: "Rozdělení provize nevyplněno",
};

const CommissionDistributionStatusChip = ({
	contract,
	provisionStatus,
}: CommissionDistributionStatusChipProps) => {
	const [dialogOpen, setDialogOpen] = useState(false);

	return (
		<>
			<StatusChip
				label={
					<>
						<Typography variant="inherit" component="span">
							Provize
						</Typography>
						<DeviceHubOutlined sx={{ fontSize: "16px" }} />
					</>
				}
				title={provisionTooltips[provisionStatus]}
				onClick={() => setDialogOpen(true)}
				sx={{
					cursor: "pointer",
					...essentialsStatusColors[provisionStatus],
				}}
			/>
			{dialogOpen && (
				<CommissionDistributionDialog
					contract={contract}
					onClose={() => setDialogOpen(false)}
				/>
			)}
		</>
	);
};
